/**
 * Typisierte Hooks für `/api/v1/tenants` (Spec 03, `domains/tenants`).
 *
 * `fetch` steht ausschließlich hier — nie in einer Komponente
 * (`apps/web/CLAUDE.md`). Query-Keys kommen ausschließlich aus
 * `lib/query-keys.ts`; jede Mutation invalidiert über ein Präfix, nie über
 * einzeln aufgezählte Keys.
 *
 * Kein „Anlegen und noch eins" bei Mietern (Spec 03, „Sheet Mieter anlegen":
 * bewusste Abweichung vom 02a-Muster) — deshalb fehlt hier die optimistische
 * Baumzeilen-Logik aus `features/properties/api.ts`. Ein Mieter steht ohnehin
 * nie im Baum (Spec 03, AK-179).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'

export type TenantDetail = components['schemas']['TenantDetail']
export type TenantCreate = components['schemas']['TenantCreate']
export type TenantUpdate = components['schemas']['TenantUpdate']
export type LegalFormOption = components['schemas']['LabeledValue']
export type SalutationOption = components['schemas']['LabeledValue']

/** Antwort von `GET /entities/{id}/archive-preview` — dieselbe wie in
 * `features/properties/api.ts`, hier noch einmal lokal definiert:
 * `features/*` dürfen einander nicht importieren (`apps/web/CLAUDE.md`). */
export type ArchivePreview = components['schemas']['ArchivePreview']

interface QueryOptions {
  readonly enabled?: boolean
}

export function useTenantDetail(tenantId: string, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.tenants.detail(tenantId),
    queryFn: ({ signal }) => request<TenantDetail>(`/tenants/${tenantId}`, { signal }),
    enabled: options.enabled ?? true,
  })
}

/** `GET /tenants/legal-forms` — ändert sich zur Laufzeit nie, zwei feste
 * Werte aus einem Check-Constraint (Spec 03, „Datenmodell"). */
export function useLegalForms() {
  return useQuery({
    queryKey: keys.tenants.legalForms,
    queryFn: ({ signal }) => request<LegalFormOption[]>('/tenants/legal-forms', { signal }),
    staleTime: Infinity,
  })
}

/** `GET /tenants/salutations` — dito, drei feste Werte (Spec 03, Entscheidung 7). */
export function useSalutations() {
  return useQuery({
    queryKey: keys.tenants.salutations,
    queryFn: ({ signal }) => request<SalutationOption[]>('/tenants/salutations', { signal }),
    staleTime: Infinity,
  })
}

interface CreateTenantArgs {
  readonly body: TenantCreate
}

export function useCreateTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ body }: CreateTenantArgs) =>
      request<TenantDetail>('/tenants', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      // Neben der Mieterauswahl enthält die flache Objektliste neue Mieter.
      void queryClient.invalidateQueries({ queryKey: keys.tenants.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface UpdateTenantArgs {
  readonly tenantId: string
  readonly body: TenantUpdate
  readonly ifMatch: string
}

export function useUpdateTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tenantId, body, ifMatch }: UpdateTenantArgs) =>
      request<TenantDetail>(`/tenants/${tenantId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      // Zwei Präfixe: `tenants` für die eigene Detailansicht und die
      // Mieterauswahl, `entities` für den Kopf der Objektansicht
      // (`tenant-label` ist `entities.label`, folgt `display_name` über
      // `trg_sync_label_tenants`) — dasselbe Muster wie bei Grundstück,
      // Gebäude und Einheit.
      void queryClient.invalidateQueries({ queryKey: keys.tenants.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

interface ArchiveTenantArgs {
  readonly tenantId: string
  readonly ifMatch: string
}

export function useArchiveTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tenantId, ifMatch }: ArchiveTenantArgs) =>
      request<TenantDetail>(`/tenants/${tenantId}/archive`, { method: 'POST', ifMatch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.tenants.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

export function useUnarchiveTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tenantId, ifMatch }: ArchiveTenantArgs) =>
      request<TenantDetail>(`/tenants/${tenantId}/unarchive`, { method: 'POST', ifMatch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.tenants.all })
      void queryClient.invalidateQueries({ queryKey: keys.entities.all })
    },
  })
}

/** `GET /entities/{id}/archive-preview` — als Mutation statt `useQuery`, wie
 * in `features/properties/api.ts`: Der Aufruf ist ein einmaliger
 * Klick-Trigger, kein Zustand, der gemountet bleiben soll. */
export function useArchivePreview() {
  return useMutation({
    mutationFn: (entityId: string) => request<ArchivePreview>(`/entities/${entityId}/archive-preview`),
  })
}
